import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Loader from "components/Loader";
import PermissionDenied from "components/PermissionDenied";

import { replaceSegmentsFromRoutes } from "helpers/replaceSegmentsFromRoutes";
import { useGetAccess } from "hooks/useGetAccess";
import { useGetEntityParams } from "hooks/useGetEntityParams";

import { RouteType } from "types";

interface AccessRouteProps {
  route: RouteType;
  children: React.ReactNode;
}

const AccessRoute = ({ route, children }: AccessRouteProps) => {
  const navigate = useNavigate();
  const { loading, givenAccess } = useGetAccess({
    requiredAccessRights: route.requiredAccessRights,
    superAdminOnly: route.superAdminOnly,
  });
  const entityParams = useGetEntityParams();
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);

    if (loading || entityParams === undefined) return;
    if (!route.redirectWhenPermissionDenied || givenAccess) {
      return setBusy(false);
    }

    let routeToRedirect = route.redirectWhenPermissionDenied;
    if (entityParams.length) {
      const routeData = entityParams.reduce(
        (data: { params: string[]; replacer: string[] }, entity) => {
          return {
            params: [...data.params, entity.key],
            replacer: [...data.replacer, entity.value],
          };
        },
        {
          params: [],
          replacer: [],
        }
      );

      routeToRedirect = replaceSegmentsFromRoutes({
        params: routeData.params,
        replacer: routeData.replacer,
        route: route.redirectWhenPermissionDenied,
      });
    }

    navigate(routeToRedirect);
  }, [route, entityParams, loading, givenAccess, navigate]);

  if (loading || busy) {
    return <Loader />;
  }

  if (!givenAccess) {
    return <PermissionDenied />;
  }

  return <>{children}</>;
};

export default AccessRoute;
